import { effectiveImageBoxWidthPx, storedImageWidthPx } from './image-width';
import type { ImageBlock, ImageIntrinsicSize } from './types';

export const MIN_INTRINSIC_IMAGE_DIMENSION = 1;
export const MAX_INTRINSIC_IMAGE_DIMENSION = 8192;

export type AutoImageReservedBox = {
  aspectRatio: string;
  minHeightPx: number | undefined;
  wrapperWidthPx: number | undefined;
  wrapperWidthAuto: boolean;
};

export function isValidIntrinsicDimension(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= MIN_INTRINSIC_IMAGE_DIMENSION &&
    value <= MAX_INTRINSIC_IMAGE_DIMENSION
  );
}

export function imageIntrinsicSizeFromDimensions(
  width: unknown,
  height: unknown,
): ImageIntrinsicSize | null {
  if (!isValidIntrinsicDimension(width) || !isValidIntrinsicDimension(height)) {
    return null;
  }

  return { width, height };
}

export function resolveImageIntrinsicSize(
  block: ImageBlock,
): ImageIntrinsicSize | null {
  if (block.intrinsicSize != null) {
    const { width, height } = block.intrinsicSize;
    if (width > 0 && height > 0) {
      return { width, height };
    }
  }

  return null;
}

export function computeAutoImageReservedBox(
  block: ImageBlock,
  contentMaxWidth: number | undefined,
  intrinsicSize: ImageIntrinsicSize,
): AutoImageReservedBox {
  const aspectRatio = `${intrinsicSize.width} / ${intrinsicSize.height}`;

  let minHeightPx: number | undefined;
  if (contentMaxWidth != null && contentMaxWidth > 0) {
    const boxWidth = effectiveImageBoxWidthPx(block, contentMaxWidth);
    minHeightPx = Math.round(
      (boxWidth * intrinsicSize.height) / intrinsicSize.width,
    );
  }

  const storedWidthPx = storedImageWidthPx(block);

  return {
    aspectRatio,
    minHeightPx,
    wrapperWidthPx:
      storedWidthPx !== null &&
      contentMaxWidth != null &&
      contentMaxWidth > 0
        ? effectiveImageBoxWidthPx(block, contentMaxWidth)
        : storedWidthPx ?? undefined,
    wrapperWidthAuto: false,
  };
}

export function autoImageReservedWrapperStyle(
  block: ImageBlock,
  reservedBox: AutoImageReservedBox,
): {
  display: 'block';
  width: string;
  maxWidth: string;
  aspectRatio: string;
  minHeight?: number;
  marginLeft: number | 'auto';
  marginRight: number | 'auto';
} {
  return {
    display: 'block',
    width: reservedBox.wrapperWidthAuto
      ? 'auto'
      : reservedBox.wrapperWidthPx != null
        ? `${reservedBox.wrapperWidthPx}px`
        : '100%',
    maxWidth: '100%',
    aspectRatio: reservedBox.aspectRatio,
    ...(reservedBox.minHeightPx != null
      ? { minHeight: reservedBox.minHeightPx }
      : {}),
    marginLeft: block.align === 'left' ? 0 : 'auto',
    marginRight: block.align === 'right' ? 0 : 'auto',
  };
}
